import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Lock, Unlock, RotateCcw, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_VARIANT_LABELS = {
  A: "I help universities and major events design programs that outlast the person running them.",
  B: "From a carpark to 8,000+ weekly students — I design programs that outlast their leader.",
};

/** Small dashboard tile that shows the current state of the hero-role A/B
 * experiment: exposure and conversion counts per variant, current conversion
 * rate, lock state, and quick admin actions (force winner, unlock, reset). */
export default function ABExperimentPanel({
  experiment = "hero-role",
  variantLabels = HERO_VARIANT_LABELS,
  title = "Auto-promote the winning line",
  eyebrow,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    axios
      .get(`${API}/ab/${experiment}/status`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load A/B status"))
      .finally(() => setLoading(false));
  }, [experiment]);

  useEffect(() => { load(); }, [load]);

  const call = async (path, method = "post", body = null, label = "") => {
    setBusy(label || path);
    try {
      const r = await axios({
        method,
        url: `${API}/admin/ab/${experiment}/${path}`,
        data: body,
        withCredentials: true,
      });
      toast.success(r.data?.message || "Done");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const forceWinner = (variant) => call("force-winner", "post", { variant }, `force-${variant}`);
  const unlock = () => call("unlock", "post", null, "unlock");
  const reset = () => {
    if (typeof window !== "undefined" && !window.confirm("Reset all exposures + conversions for this experiment? This can't be undone.")) return;
    call("reset", "post", null, "reset");
  };

  if (loading || !data) {
    return (
      <div
        data-testid="ab-panel-loading"
        className="border border-[#343437] bg-[#151517]/60 p-6 flex items-center gap-3 text-sm text-[#A9A9A6]"
      >
        <Loader2 size={14} className="animate-spin" /> Loading A/B experiment…
      </div>
    );
  }

  const stats = data.stats || {};
  const cfg = data.config || {};
  const lock = data.lock;
  const winner = lock?.winner_variant;
  // Derive variant keys from the provided variantLabels so 3-way (and n-way)
  // experiments render every card before any exposures accumulate.
  const variantKeys = Array.from(new Set([...Object.keys(variantLabels), ...Object.keys(stats)]));
  const displayEyebrow = eyebrow || `A/B experiment · ${experiment}`;

  return (
    <div data-testid="ab-experiment-panel" className="border border-[#343437] bg-[#151517]/60 p-6 lg:p-8">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <div>
          <p className="eyebrow">{displayEyebrow}</p>
          <h2 className="mt-3 font-display text-2xl lg:text-3xl">{title}</h2>
        </div>
        {lock ? (
          <span
            data-testid="ab-panel-lock-badge"
            className="inline-flex items-center gap-1.5 border border-[#D8A936] text-[#D8A936] px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase"
          >
            <Lock size={12} /> Locked · {lock.reason === "manual" ? "manual" : "auto"}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 border border-[#343437] text-[#A9A9A6] px-3 py-1.5 text-[10px] tracking-[0.14em] uppercase">
            <Unlock size={12} /> Live · collecting data
          </span>
        )}
      </div>
      <p className="mt-3 text-sm text-[#A9A9A6] leading-relaxed max-w-3xl">
        The winning variant is locked automatically when each side has at least{" "}
        <span className="text-[#FAF9F6]">{cfg.min_exposures} exposures</span> and one beats
        the other by <span className="text-[#FAF9F6]">{Math.round((cfg.lift_threshold || 0) * 100)}%</span>{" "}
        relative lift. New visitors then only see the winner.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {variantKeys.map((v) => {
          const s = stats[v] || { exposures: 0, conversions: 0, rate: 0 };
          const isWinner = winner === v;
          return (
            <div
              key={v}
              data-testid={`ab-variant-${v}`}
              className={`border p-5 ${isWinner ? "border-[#D8A936] bg-[#D8A936]/5" : "border-[#343437] bg-[#0B0B0C]"}`}
            >
              <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
                <p className="font-display text-lg text-[#FAF9F6]">Variant {v}</p>
                {isWinner && (
                  <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.14em] uppercase text-[#D8A936]">
                    <Trophy size={12} /> Winner
                  </span>
                )}
              </div>
              <p className="text-xs text-[#A9A9A6] leading-relaxed italic mb-4">
                "{variantLabels[v] || "—"}"
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B]">Exposures</p>
                  <p className="mt-1 font-display text-xl text-[#FAF9F6]">{s.exposures}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B]">Conversions</p>
                  <p className="mt-1 font-display text-xl text-[#FAF9F6]">{s.conversions}</p>
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.14em] uppercase text-[#6E6E6B]">Rate</p>
                  <p className="mt-1 font-display text-xl text-[#D8A936]">{(s.rate * 100).toFixed(1)}%</p>
                </div>
              </div>
              {!lock && (
                <button
                  type="button"
                  onClick={() => forceWinner(v)}
                  disabled={busy === `force-${v}`}
                  data-testid={`ab-force-winner-${v}`}
                  className="mt-4 text-[10px] tracking-[0.14em] uppercase text-[#A9A9A6] hover:text-[#D8A936] transition-colors disabled:opacity-40"
                >
                  {busy === `force-${v}` ? "Locking…" : "Manually lock this variant →"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap">
        {lock && (
          <button
            type="button"
            onClick={unlock}
            disabled={busy === "unlock"}
            data-testid="ab-unlock"
            className="inline-flex items-center gap-1.5 border border-[#343437] px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase text-[#A9A9A6] hover:text-[#FAF9F6] hover:border-[#6E6E6B] transition-colors disabled:opacity-40"
          >
            {busy === "unlock" ? <Loader2 size={12} className="animate-spin" /> : <Unlock size={12} />}
            Unlock
          </button>
        )}
        <button
          type="button"
          onClick={reset}
          disabled={busy === "reset"}
          data-testid="ab-reset"
          className="inline-flex items-center gap-1.5 border border-[#343437] px-3 py-1.5 text-[11px] tracking-[0.14em] uppercase text-[#A9A9A6] hover:text-[#EF4444] hover:border-[#EF4444] transition-colors disabled:opacity-40"
        >
          {busy === "reset" ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
          Reset experiment
        </button>
      </div>
    </div>
  );
}
